import AppInfoFooter from "@/components/AppInfoFooter"
import Button from "@/components/Button"
import InputErrorMessage from "@/components/InputErrorMessage"
import { EmailInput } from "@/components/InputFields"
import InputLabel from "@/components/InputLabel"
import Link from "@/components/Link"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import { useSendForgotPasswordEmailMutation } from "@/data/queries"
import { forgotPasswordSchema } from "@/schemas/zod-schemas"
import cn from "@/utils/cn"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { useLocation } from "react-router"

type ForgotPasswordPageStateParams = {
  email?: string
}

type ForgotPasswordFormData = {
  email: string
}

const ForgotPasswordPage = () => {
  const locationState = useLocation().state as ForgotPasswordPageStateParams
  const { register, handleSubmit, formState, getValues } =
    useForm<ForgotPasswordFormData>({
      mode: "onChange",
      defaultValues: { email: locationState?.email ?? "" },
      resolver: zodResolver(forgotPasswordSchema),
    })

  const {
    mutateAsync: sendForgotPasswordEmail,
    isSuccess: isForgotPasswordMailSent,
    isPending: isSendingForgotPasswordEmail,
    error: sendForgotPasswordEmailError,
  } = useSendForgotPasswordEmailMutation()

  const { errors } = formState

  const onSubmit = async (formData: ForgotPasswordFormData) => {
    const { email } = formData
    await sendForgotPasswordEmail({ email })
  }

  return (
    <Page className="bg-gray-50">
      <div className="flex h-full flex-col justify-between">
        <div
          className={cn(
            "mx-auto mt-10 w-full max-w-[480px] rounded-xl bg-white p-12 shadow-2xl"
          )}
        >
          {isForgotPasswordMailSent ? (
            <div className="flex flex-col items-center gap-4">
              <h2 className="mb-4 text-center text-3xl font-bold text-primary/90">
                Reset password email sent successfully
              </h2>
              <span
                className="material-icons-outlined text-green-600"
                style={{ fontSize: "100px" }}
              >
                check_circle
              </span>
              <p className="my-4 text-pretty text-center text-primary/90">
                A reset password email was sent to{" "}
                <span className="text-blue-500">{getValues("email")}</span> .
                <br />
                Please check your email.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
              <h2 className="mb-4 text-center text-3xl font-bold text-primary/90">
                Forgot password
              </h2>
              <h3 className="mb-8 text-pretty text-center text-lg text-primary/90">
                We will send a password reset link to your email address
              </h3>

              {sendForgotPasswordEmailError && (
                <div className="my-4 flex w-full items-center rounded-md border border-red-200 bg-red-100 p-4">
                  <span className="material-icons me-4 text-red-600">
                    error
                  </span>
                  <p className="text-red-600">
                    Something went wrong. Please try again later, or contact the
                    dev team.
                  </p>
                </div>
              )}

              <div className="flex flex-col gap-1">
                <InputLabel htmlFor="email" className="ml-1">
                  Email
                </InputLabel>
                <EmailInput
                  id="email"
                  placeholder="Email address"
                  autoComplete="username"
                  disabled={isSendingForgotPasswordEmail}
                  {...register("email")}
                  isError={!!errors?.email?.message}
                />
                <InputErrorMessage
                  message={errors?.email?.message}
                  className="ml-1"
                />
              </div>

              <div className="flex">
                <Link
                  to="/login"
                  state={{ email: getValues("email") }}
                  replace={true}
                  className="mt-6 inline-flex items-center hover:no-underline"
                >
                  <span className="material-icons">chevron_left</span>Back to
                  Login
                </Link>
              </div>

              <Button
                variant="primary"
                type="submit"
                disabled={isSendingForgotPasswordEmail}
                className="mt-4 flex w-full gap-3 text-base"
              >
                {isSendingForgotPasswordEmail && <Spinner />}
                Reset password
              </Button>
            </form>
          )}
        </div>
        <AppInfoFooter className="mt-auto p-4" />
      </div>
    </Page>
  )
}

export default ForgotPasswordPage
